import axios from "axios";
import sessionStore from './session-store';
import { createSessionCache } from './session-cache';
import { createMutationKeys } from './mutation-keys';
const validatedSessions = createSessionCache();
const mutationKeys = createMutationKeys();

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

const ADMIN_KEY = "mb_admin_token";
const USER_KEY = "mb_user_token";

const readToken = (key) => {
  try {
    return sessionStorage.getItem(key) || localStorage.getItem(key);
  } catch (e) {
    console.error("Storage access error:", e);
    return null;
  }
};
const writeToken = (key, token, remember = false) => {
  try {
    if (remember) {
      localStorage.setItem(key, token);
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, token);
      localStorage.removeItem(key);
    }
  } catch (e) {
    console.error("Storage access error:", e);
    // If persistent storage is unavailable, keep the current browser session usable.
    try { sessionStorage.setItem(key, token); } catch {}
  }
};
const clearToken = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.error("Storage access error:", e);
  }
  sessionStorage.removeItem(key);
};

export const auth = {
  getAdmin: () => readToken(ADMIN_KEY),
  setAdmin: (t, remember = true) => { writeToken(ADMIN_KEY, t, true); sessionStore.remember('admin', t); },
  clearAdmin: () => { validatedSessions.clear(auth.getAdmin()); clearToken(ADMIN_KEY); },
  getUser: () => readToken(USER_KEY),
  setUser: (t, remember = true) => { writeToken(USER_KEY, t, true); sessionStore.remember('user', t); },
  clearUser: () => { validatedSessions.clear(auth.getUser()); clearToken(USER_KEY); },
  remembered: () => sessionStore.read(),
  forget: (kind, token) => sessionStore.forget(kind, token),
  activate: (kind) => { const token = kind === 'admin' ? readToken(ADMIN_KEY) : readToken(USER_KEY); if (token) sessionStore.remember(kind, token); },
  selectUser: (id) => { const saved = sessionStore.read().accounts[`user:${id}`]; if (!saved) return false; auth.setUser(saved.token); return true; },
  async logout(kind) {
    const token = kind === 'admin' ? auth.getAdmin() : auth.getUser();
    const { data } = await publicApi.post('/auth/logout', {}, { headers: { Authorization: `Bearer ${token}` } });
    const userId = sessionStore.metadata(token).user_id;
    Object.values(sessionStore.read().accounts).forEach(saved => {
      if (saved.token === token || (userId && saved.userId === userId) || data.revoked?.includes(sessionStore.metadata(saved.token).sid)) sessionStore.forget(saved.kind, saved.token);
    });
    auth.clearUser(); auth.clearAdmin();
  },
};

export const adminApi = axios.create({ baseURL: API });
adminApi.interceptors.request.use((cfg) => {
  if (!['get','head'].includes(cfg.method) && !cfg.headers['Idempotency-Key']) cfg.headers['Idempotency-Key'] = cfg.data?.request_id || crypto.randomUUID();
  const t = auth.getAdmin();
  if (t && !cfg.headers.Authorization) cfg.headers.Authorization = `Bearer ${t}`;
  return mutationKeys.prepare(cfg, cfg.headers.Authorization || t);
});

export const userApi = axios.create({ baseURL: API });
userApi.interceptors.request.use((cfg) => {
  if (!['get','head'].includes(cfg.method) && !cfg.headers['Idempotency-Key']) cfg.headers['Idempotency-Key'] = cfg.data?.request_id || crypto.randomUUID();
  const viewing = window.location.pathname.startsWith('/admin/view-user/');
  const t = viewing ? sessionStorage.getItem('mb_view_token') : auth.getUser();
  if (t && !cfg.headers.Authorization) cfg.headers.Authorization = `Bearer ${t}`;
  return mutationKeys.prepare(cfg, cfg.headers.Authorization || t);
});

export const publicApi = axios.create({ baseURL: API });

adminApi.interceptors.response.use(response => {
  if (response.config.url === '/admin/change-password' && response.data?.token) auth.setAdmin(response.data.token);
  return response;
});
userApi.interceptors.response.use(response => {
  if (response.config.url === '/user/change-password' && response.data?.token) auth.setUser(response.data.token);
  return response;
}, error => {
  if (error?.response?.status === 428 && !window.location.pathname.startsWith('/admin/view-user/') && window.location.pathname !== '/user/password-required') window.location.assign('/user/password-required');
  return Promise.reject(error);
});

export const invalidSession = error => [401, 403, 404].includes(error?.response?.status);

export function validateSession(kind, token, force = false) {
  return validatedSessions.verify(token, async () => {
    const api = kind === 'admin' ? adminApi : userApi;
    const config = { headers: { Authorization: `Bearer ${token}` } };
    // Only legacy JWTs need upgrading. A persistent token is authenticated by
    // the protected profile endpoint itself, saving a startup round trip.
    const data = sessionStore.metadata(token).sid || validatedSessions.peek(token)
      ? { token } : (await api.post(`/auth/remember-${kind}`, {}, config)).data;
    const profile = await api.get(kind === 'admin' ? '/admin/access' : '/user/me', { headers: { Authorization: `Bearer ${data.token}` } });
    return { token: data.token, profile: profile.data };
  }, force);
}
export const sessionProfile = token => validatedSessions.peek(token)?.profile;
for (const api of [adminApi, userApi]) api.interceptors.response.use(response => { mutationKeys.settle(response.config, response.status); return response; }, error => {
  mutationKeys.settle(error.config, error.response?.status);
  // Ordinary permission denials must not sign a valid user out.
  if (error?.response?.status === 401) {
    const token = String(error.config?.headers?.Authorization || '').replace(/^Bearer /, '');
    validatedSessions.clear(token);
    window.dispatchEvent(new CustomEvent('mb:session-invalid', { detail: { token } }));
  }
  return Promise.reject(error);
});

export const fileUrl = (path) => (path ? `${API}/files/${path}` : null);

export const formatUSD = (n) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0));

export const LBP_INCREMENT = 5000;
export const roundLBP = (n) => Math.round(Number(n || 0) / LBP_INCREMENT) * LBP_INCREMENT;
export const usdToLBP = (usd, exchangeRate = 90000) => roundLBP(Number(usd || 0) * Number(exchangeRate || 90000));

export const formatLBP = (n) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(roundLBP(n));

export const arDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("ar-EG", {
      year: "numeric", month: "long", day: "2-digit", hour: "2-digit", minute: "2-digit",
    }).format(d);
  } catch {
    return iso;
  }
};
